import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  styled,
  useTheme,
} from "@mui/material";
import { useField } from "formik";
import PTDynamicFormField from "./PTDynamicFormField";
import { useLanguage } from "../../hooks";
import { translate } from "../../utils/translate";
import { useTranslation } from "react-i18next";

const StyledSelect = styled(Select)(({ theme, height }) => ({
  height: `${height}px`,
  "&:focus": {
    backgroundColor: "transparent",
  },
  "&:hover": {
    backgroundColor: "transparent",
  },
}));

const CustomBox = styled(Box)`
  overflow-x: auto;
  max-width: 100%;
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
  margin-top: 2px;
`;

export const PTSelectInput = ({ ...props }) => {
  return (
    <PTDynamicFormField {...props}>
      <PTSelectInputUI {...props} />
    </PTDynamicFormField>
  );
};

export const PTSelectInputUI = ({
  name,
  label,
  options,
  required,
  error,
  height = 40,
  valueRenderer = (item) => item.value,
  isDisabled = (inputName)=> false,
  ...restProps
}) => {
  const disabled = isDisabled(name)
  const theme = useTheme();
  const {t} = useTranslation();
  const { currentLang } = useLanguage();
  const [field, meta] = useField(name);
  if (!options) throw new Error("options prop is required for SelectInput");
  const borderStyles = {
    backgroundColor: disabled ? "#e0e0e0" : "#FFFFFF",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        border: error ? "1px solid red" : "1px solid gray",
      },
      "&:hover fieldset": {
        border: error ? "1px solid red" : "1px solid gray",
      },
      "&.Mui-focused fieldset": {
        border: error ? "1px solid red" : "1px solid gray",
      },
    },
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main,
    },
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main,
      ...(currentLang === "ar" && { left: 7, right: "auto" }),
    },
  };
        
  return (
    <FormControl fullWidth error={meta.touched && !!meta.error}>
      <StyledSelect
        {...field}
        value={field.value || ""}
        required={required}
        disabled={disabled}
        sx={{
          height: `${height}px`,
          backgroundColor: "#FFFFFF",
          ...borderStyles,
        }}
        height={height}
        fullWidth
        displayEmpty
        renderValue={(selectedValue) => {
          // Handle the case when selectedValue is an object
          if (selectedValue && typeof selectedValue === 'object') {
            // Check if it has paramLibelle property (from your data structure)
            if (selectedValue?.paramLibelle) {
              const typeKey = `licence.typeDemande.${selectedValue.paramValue}`;
              const statusKey = `licence.status.${selectedValue.paramValue}`;
              const typeTranslation = t(typeKey);
              const statusTranslation = t(statusKey);
            
              if (!typeTranslation.startsWith("licence.typeDemande.")) {
                return typeTranslation;
              } else if (!statusTranslation.startsWith("licence.status.")) {
                return statusTranslation;
              } else {
                return selectedValue.paramLibelle;
              }
            }            
            
            // Try to find the option from options using the valueRenderer
            const selectedOption = options.find(option => {
              // Try to match by id if available
              if (selectedValue.id && option.value && option.value.id) {
                return selectedValue.id === option.value.id;
              }
              return false;
            });
            
            return selectedOption ? selectedOption.label : JSON.stringify(selectedValue);
          }
          
          // Original logic for non-object values
          const selectedOption = options.find(
            option => JSON.stringify(valueRenderer(option)) === JSON.stringify(selectedValue)
          );
          
          return selectedOption ? selectedOption.label : selectedValue;
        }}
        error={error ? true : false}
        {...restProps}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={valueRenderer(option)}>
            {option.label}
          </MenuItem>
        ))}
      </StyledSelect>
      <FormHelperText>{meta.touched && translate(meta.error)}</FormHelperText>
    </FormControl>
  );
};

export default PTSelectInput;
